import { RefinanceStore } from './../RefinanceStore';

export const RefinanceStepMixin = {
    data() {
        return {
            store: RefinanceStore,
            selectedRefiOption: RefinanceStore.data.advancedRefi ? 'advanced' : 'standard',
            gTagPageName: 'introxl-refi-cash-back',
            loading: false,
            cashBackAmount: 0, // Will be set to minCashBack in mounted()
            errors: {
                cash_back_amount: null,
                global: null,
            }
        };
    },
    computed: {
        storeData() {
            return this.store.data;
        },

        minCashBack() {
            return 0.01;
        },

        effectiveMaxCashBack() {
            if (this.selectedRefiOption === 'advanced' && this.storeData.advancedRefiMaxCashBack !== null) {
                return this.storeData.advancedRefiMaxCashBack;
            }
            return this.storeData.maxCashBack;
        },

        phase2TargetPayments() {
            if (this.storeData.totalPayments > 0) {
                return this.storeData.totalPayments;
            }

            if (this.storeData.targetPct > 0 && this.storeData.totalSchedulePayments > 0) {
                return Math.ceil(this.storeData.totalSchedulePayments * this.storeData.targetPct / 100);
            }
            return this.storeData.totalSchedulePayments;
        },

        // Something is actually unlockable only when a lower rate (or a milestone cash bonus)
        // exists for this borrower. At the rate floor / paid-in-full band both are absent, so
        // "N of M payments to unlock rewards" progress copy must not render — the fabricated
        // fallback target would show impossible counts (e.g. 200/100 completed, -100 away).
        hasUnlockableRewards() {
            return Boolean(this.storeData.previewBetterRate) || this.storeData.previewBalanceBonus > 0;
        },

        newLoanAmount() {
            return this.storeData.payoff + this.cashBackAmount;
        },

        canRefinance() {
            // Cannot refinance if no cash back available (payoff >= loanAmount)
            if (this.effectiveMaxCashBack <= 0) return false;
            // Cannot refinance if 0 payments made (nothing to cash back)
            if (this.storeData.paidPayments <= 0) return false;

            if (this.storeData.hasMissedPayments) {
                return false;
            }

            return true;
        },

        maxLoanAmount() {
            return this.storeData.loanAmount + this.storeData.previewBalanceBonus; // $1.5M cap for now to prevent overflow issues in UI
        },

        // Total cash available at the next milestone: today's cash back plus the
        // extra unlocked at the milestone. Always >= the top card's maxCashBack.
        milestoneCashBack() {
            return this.storeData.maxCashBack + this.storeData.previewBalanceBonus;
        }
    },
    methods: {
        next() {
            this.store.next();
        },

        back() {
            this.store.prev();
        },

        updateStore(newData) {
            this.store.update(newData);
        },

        paymentsAway() {
            return Math.max(0, this.phase2TargetPayments - this.storeData.paidPayments);
        },

        resetErrors() {
            this.errors.cash_back_amount = null;
            this.errors.global = null;
        },

        submitRefi() {
            this.resetErrors();
            this.loading = true;

            const requestData = {
                cash_back_amount: this.cashBackAmount,
            };

            if (this.storeData.advancedRefi) {
                requestData.use_advanced_refi = true;
            }

            axios.post('/introxl/refi-submit/' + this.storeData.loan.id, requestData)
                .then(() => {
                    this.next();
                    this.store.lockNavigation();
                })
                .catch(error => {
                    this.handleError(error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        handleError(error) {
            if (!error.response) {
                this.errors.global = 'An unexpected error occurred. Please try again later.';
                return;
            }

            // Throttle (429): never surface Laravel's raw "Too Many Attempts." string. Show a
            // friendly retry message instead so a rate limit reads as guidance, not a cryptic error.
            if (error.response.status === 429) {
                this.errors.global = "You've tried a few times in a row. Please wait a minute and try again, or return to the portal.";
                return;
            }

            const data = error.response.data;

            // Any 422 from the refi submit is a server-side block (no payroll / disconnected /
            // revoked bank), so reload: showRefiPage() re-evaluates and serves the correct page
            // (the "no payroll → Connect a Bank with Pay" guidance, or the offer again if the
            // borrower has since recovered) instead of leaving a stuck Confirm + red bar.
            //
            // The Confirm page has no editable input — cash_back_amount is chosen on the earlier
            // configuration step and clamped by its slider — so the cash_back_amount bounds rule
            // is unreachable for a normal user; only a tampered POST could trip it, and a reload
            // is the right answer there too. Other statuses (500, etc.) keep their inline message
            // rather than risk a reload loop on a transient fault.
            if (error.response.status === 422) {
                window.location.reload();
                return;
            }

            this.errors.global = (data && data.message) || 'An unexpected error occurred. Please try again later.';
        }
    },

    watch: {
        cashBackAmount(newValue) {
            // Skip clamping when there's no valid cash back range (e.g. eligible=false)
            if (this.effectiveMaxCashBack <= 0) return;

            // Ensure cash back stays within bounds
            if (newValue < this.minCashBack) {
                this.cashBackAmount = this.minCashBack;
            } else if (newValue > this.effectiveMaxCashBack) {
                this.cashBackAmount = this.effectiveMaxCashBack;
            } else {
                // Persist to store so next page can restore it
                this.store.update({ cashBackAmount: newValue });
            }
        },
        selectedRefiOption() {
            // Clamp cash back when switching options
            if (this.cashBackAmount > this.effectiveMaxCashBack) {
                this.cashBackAmount = this.effectiveMaxCashBack;
            }
        }
    },

    created() {
        if (typeof this.gTagEvent === 'function') {
            this.gTagEvent(this.gTagPageName, {
                event_category: 'onboarding',
                event_label: this.gTagPageName,
            });
        }
    },

    mounted() {
        // Restore from store if already set (e.g. navigating back/forward between pages)
        if (this.storeData.cashBackAmount > 0) {
            this.cashBackAmount = this.storeData.cashBackAmount;
        } else if (RefinanceStore.data.advancedRefi) {
            // For advanced refi (Phase 2), set cash back to max (no slider)
            this.cashBackAmount = this.storeData.advancedRefiMaxCashBack;
        } else {
            // Initialize cash back to minimum (one payment)
            this.cashBackAmount = this.minCashBack;
        }
    },
};